import {
  Callout,
  Checkbox,
  FormLayout,
  GridFieldGroup,
  Heading,
  Link,
  LoadingSpinner,
  MaskedInput,
  Paragraph,
  Reservation,
  ReservationCard,
  Select,
  SubmitButton,
  TextInput,
} from '@expressable/syntax-ds';
import { zodResolver } from '@hookform/resolvers/zod';
import config from 'config';
import { ContactRelationship } from 'domains/client';
import { SignupErrorCode } from 'domains/evaluations/evaluationSchedule';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import InsuranceSections from 'domains/signupDirect/Evaluation/Insurance/InsuranceSections';
import PrivatePaySections from 'domains/signupDirect/Evaluation/PrivatePay/PrivatePaySections';
import { submitEvalForm } from 'domains/signupDirect/Evaluation/submitEvalForm';
import {
  getRelationshipOptions,
  INSURANCE_OPTION,
  OTHER_INSURANCE_OPTION,
  therapyTargetValues,
} from 'domains/signupDirect/misc/constants';
import { usePeriodicEvaluationFormSave } from 'domains/signupDirect/misc/localStorage';
import { useSignupNavigationGuard } from 'domains/signupDirect/misc/useSignupNavigationGuard';
import {
  EvaluationForm,
  EvaluationFormType,
  paymentTypeIsInsurance,
  shouldHideContinueButtonForEligibility,
} from 'domains/signupDirect/schemas/evaluationSchema';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { noop } from 'lodash';
import { useCallback, useState } from 'react';
import { FieldErrors, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from 'router/routes';
import { getReservationScheduleProps } from 'utils/date';
import { SignupError } from 'utils/errors/debuggableError';
import { useFieldErrors } from 'utils/forms/fieldErrors';
import { useAutoScrollToTop } from 'utils/hooks/useAutoScrollToTop/useAutoScrollToTop';
import { SignupLoginGate } from '../Auth/LoginGate';
import { useSignupRoutingRules } from '../businessLogic/hooks';
import { isSplitEvaluationSchema } from '../schemas/evaluationScheduleSchema';
import { reportMultipleInvalidSignupEvalForm } from '../sentry/signupSentry';
import { TherapistReservationCard } from '../SyntaxComps/TherapistReservationCard';
import { getEvaluationFormDefaultValues } from './defaultValues';
import { SignupEmailErrorCallout } from './EmailError/EmailError';
import { PCPInformationInput } from './Insurance/PCP/PCPInput/PCPInformationInput';
import { PCPInputMode } from './Insurance/PCP/pcpSchemas';

const Component = () => {
  useAutoScrollToTop();
  const { filters, evaluationSchedule, incompleteEvalForm, resumedLeadState, existingContactData } =
    useSignupDirectCtx();
  const { t } = useTranslation();

  if (!filters) throw new SignupError('Expected filters to be set');
  if (!evaluationSchedule) throw new SignupError('Expected evalSelectedTimeSlot to be set');
  if (existingContactData.isLoading) throw new SignupError('Expected contact data to be already loaded');

  const methods = useForm<EvaluationFormType>({
    resolver: zodResolver(EvaluationForm),
    defaultValues: getEvaluationFormDefaultValues({
      resumedLeadState,
      evaluationSchedule,
      filters,
      incompleteEvalForm,
      existingContactData: existingContactData.contactData,
    }),
  });

  const { handleSubmit } = methods;

  return (
    <>
      <div>
        <Heading level="h1">{t('directSignup.eval.form.title')}</Heading>
        <Paragraph size="lg" noDefaultMargins>
          {t('directSignup.eval.form.subTitle')}
        </Paragraph>
      </div>

      <FormProvider {...methods}>
        <FormLayout className="mobile-xs-d:gap-e40 tablet-d:gap-e56" onSubmit={handleSubmit(noop)}>
          <FormFields />
        </FormLayout>
      </FormProvider>
    </>
  );
};

const FormFields = () => {
  const { t } = useTranslation();
  const { register, watch, getValues } = useFormContext<EvaluationFormType>();
  const [showUpdateInfo, setShowUpdateInfo] = useState(false);
  const navigate = useNavigate();
  const {
    filters,
    evaluationSchedule: evalSelectedTimeSlot,
    onSaveIncompleteEvalForm,
    isResumedLead,
    existingContactData,
  } = useSignupDirectCtx();
  if (!filters) throw new SignupError('Expected filters to be defined');
  if (!evalSelectedTimeSlot) throw new SignupError('Expected evalSelectedTimeSlot to be defined');
  if (existingContactData.isLoading) throw new SignupError('Expected existingContactData to be loaded');
  const { format } = useDisplayTimezone();
  const relationshipOptions = getRelationshipOptions(t);
  const isExistingContact = !!existingContactData.contactData;

  const email = useWatch<EvaluationFormType, 'loginContactInfo.email'>({
    name: 'loginContactInfo.email',
  });

  const isAlreadyContact = useWatch<EvaluationFormType, 'loginContactInfo.isAlreadyContact'>({
    name: 'loginContactInfo.isAlreadyContact',
  });

  useSignupNavigationGuard(() => {
    const incompleteForm = getValues();
    onSaveIncompleteEvalForm(incompleteForm);
  });

  usePeriodicEvaluationFormSave();

  const onEditSession = (session: 'first' | 'second' | 'single') => {
    const incompleteForm = getValues();
    onSaveIncompleteEvalForm(incompleteForm);

    if (session === 'single') {
      navigate(routes.signupDirect.evaluation.schedule.single);
    } else if (evalSelectedTimeSlot.type === 'split-by-slot') {
      navigate(routes.signupDirect.evaluation.schedule.split.v2[session]);
    } else if (evalSelectedTimeSlot.type === 'split-by-therapist') {
      navigate(routes.signupDirect.evaluation.schedule.split[session]);
    }
  };

  const {
    isScheduleAllowed: { allowed: isScheduleAllowed },
  } = useSignupRoutingRules();

  const isPCPRequired =
    useWatch<EvaluationFormType, 'primaryPhysician.mode'>({
      name: 'primaryPhysician.mode',
    }) !== PCPInputMode.disabled;

  return (
    <>
      {evalSelectedTimeSlot.type === 'single' && (
        <ReservationCard className="w-full" title={t('directSignup.eval.schedule.reservation.title')}>
          <Reservation
            title={t('directSignup.eval.schedule.reservation.appointment')}
            ctaLabel={isResumedLead ? undefined : t('directSignup.eval.schedule.reservation.cta')}
            onCtaClick={isScheduleAllowed ? () => onEditSession('single') : undefined}
            {...getReservationScheduleProps({
              dateTime: evalSelectedTimeSlot.timeSlot.time,
              formatFn: format,
              therapistName: evalSelectedTimeSlot.timeSlot.therapistName,
            })}
          />
        </ReservationCard>
      )}

      {isSplitEvaluationSchema(evalSelectedTimeSlot) && (
        <TherapistReservationCard
          firstSession={evalSelectedTimeSlot.firstTimeSlot.time}
          secondSession={evalSelectedTimeSlot.secondTimeSlot?.time}
          therapistName={evalSelectedTimeSlot.firstTimeSlot.therapistName}
          reservationSubtitle={t('directSignup.schedule.split.sessionType')}
          reservationTitle={t('directSignup.schedule.split.sessionName')}
          onEditFirstSession={isScheduleAllowed ? () => onEditSession('first') : undefined}
          onEditSecondSession={isScheduleAllowed ? () => onEditSession('second') : undefined}
        />
      )}

      <SignupLoginGate>
        <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
          <HeadingCombo>
            <Heading noDefaultMargins level="h5">
              {t('directSignup.sections.clientInfo.title')}
            </Heading>
            <Paragraph variant="secondary" size="md">
              {t('directSignup.sections.clientInfo.subTitle')}
            </Paragraph>
            {showUpdateInfo && (
              <Callout className="my-8" type="info">
                {t('directSignup.eligibility.tryAgainCallOut')}
              </Callout>
            )}
          </HeadingCombo>
          <GridFieldGroup>
            <TextInput
              readOnly={isResumedLead}
              data-testid="client-first-name-input"
              label={t('directSignup.labels.clientFirstName')}
              {...register('clientInfo.firstName')}
            />
            <TextInput
              readOnly={isResumedLead}
              data-testid="client-last-name-input"
              label={t('directSignup.labels.clientLastName')}
              {...register('clientInfo.lastName')}
            />

            <MaskedInput
              variant="date"
              name="clientInfo.dateOfBirth"
              hint={
                filters.paymentMethod.type === INSURANCE_OPTION || filters.paymentMethod.type === OTHER_INSURANCE_OPTION
                  ? t('directSignup.labels.dob.insurance.hint')
                  : filters.therapyTarget === therapyTargetValues[0]
                  ? t('directSignup.labels.dob.privatePay.child.hint')
                  : t('directSignup.labels.dob.privatePay.adult.hint')
              }
              data-testid="client-dob-input"
              label={t('directSignup.labels.dob')}
            />
          </GridFieldGroup>
        </FormLayout.Fields>

        <FormLayout.Fields>
          <HeadingCombo>
            <Heading noDefaultMargins level="h5">
              {t('directSignup.sections.contactInfo.title')}
            </Heading>
            <Paragraph variant="secondary" size="md">
              {t('directSignup.sections.contactInfo.subTitle')}
            </Paragraph>
          </HeadingCombo>
          <Select
            data-testid="contact-relationship-to-client-select"
            label={t('directSignup.labels.relationshipToClient')}
            name="contactInfo.relationshipToClient"
            options={relationshipOptions}
            readOnly={filters?.therapyTarget === 'adult'}
          />
          <GridFieldGroup>
            {watch('contactInfo.relationshipToClient') !== ContactRelationship.Client && (
              <>
                <TextInput
                  data-testid="contact-first-name-input"
                  label={t('directSignup.labels.contactFirstName')}
                  disabled={isExistingContact}
                  {...register('contactInfo.firstName')}
                />
                <TextInput
                  data-testid="contact-last-name-input"
                  label={t('directSignup.labels.contactLastName')}
                  disabled={isExistingContact}
                  {...register('contactInfo.lastName')}
                />
              </>
            )}

            {isAlreadyContact && (
              <TextInput
                data-testid="contact-email-input"
                label={t('directSignup.labels.email')}
                type="email"
                disabled
                value={email}
              />
            )}
            <TextInput
              data-testid="contact-phone-input"
              label={t('directSignup.labels.phone')}
              type="tel"
              disabled={isExistingContact || isResumedLead}
              {...register('contactInfo.phone')}
            />
          </GridFieldGroup>
        </FormLayout.Fields>

        <PrivatePaySections />
        <InsuranceSections showUpdateInfo={showUpdateInfo} setShowUpdateInfo={setShowUpdateInfo} />

        {isPCPRequired && <PCPInformationInput />}

        <FormLayout.GlobalErrors />
        <SubmitSection />
      </SignupLoginGate>
    </>
  );
};

const SubmitSection = () => {
  const LDClient = useLDClient();
  const flags = useFlags();
  const networkStatus = useWatch<EvaluationFormType, 'billingInfo.insuranceData.networkStatus'>({
    name: 'billingInfo.insuranceData.networkStatus',
  });
  const paymentType = useWatch<EvaluationFormType, 'billingInfo.type'>({ name: 'billingInfo.type' });
  const eligibilityResult = useWatch<EvaluationFormType, 'billingInfo.eligibilityCheckResult'>({
    name: 'billingInfo.eligibilityCheckResult',
  });

  const {
    handleSubmit,
    setError,
    register,
    getValues,
    setValue,
    formState: { submitCount, errors },
  } = useFormContext<EvaluationFormType>();
  const { t } = useTranslation();

  const { filters, resumedLeadState, hasSwitchedToPrivatePay, onSuccessfulSignup } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';

  const onInvalidSubmit = useCallback(
    (errors: FieldErrors<EvaluationFormType>) => {
      const incompleteForm = getValues();
      reportMultipleInvalidSignupEvalForm(errors, submitCount, incompleteForm as DeepPartial<EvaluationFormType>);
    },
    [submitCount, getValues],
  );

  const onClickLogin = useCallback(() => {
    setValue('loginContactInfo.isAlreadyContact', true);
  }, [setValue]);

  const fieldErrors = useFieldErrors('loginContactInfo.email', errors);

  const isEmailExistsError = fieldErrors?.includes(t('directSignup.serverErrors.EMAIL_EXISTS.base'));

  // In this case insurance form renders the submit eligibility check button
  // Hide continue button if eligibility hasn't been attempted yet OR if eligibility request failed
  if (
    paymentTypeIsInsurance(paymentType) &&
    networkStatus === 'In-network' &&
    shouldHideContinueButtonForEligibility(eligibilityResult)
  ) {
    return null;
  }
  const termsUrl = config.get('termsUrl') as string;
  const privacyPolicyUrl = config.get('privacyPolicyUrl') as string;
  const speechTermsUrl = config.get('speechTermsUrl') as string;
  const noticePrivacyUrl = config.get('privacyUrl') as string;

  return (
    <div className="flex flex-col gap-e32">
      {paymentType === 'insurance-commercial' && (
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.eval.form.riskFree.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            <Trans i18nKey="directSignup.eval.form.riskFree.description">
              <Link to={termsUrl} external>
                <></>
              </Link>
            </Trans>
          </Paragraph>
        </HeadingCombo>
      )}
      <Checkbox variant="plain" id="risk-free-checkbox" {...register('consentToTerms')}>
        <Paragraph variant="secondary" size="sm" data-testid="terms-paragraph">
          {t('directSignup.eval.form.terms.1')}

          <Link to={termsUrl} external>
            {t('directSignup.eval.form.terms.2')}
          </Link>
          {t('directSignup.eval.form.terms.3')}
          <Link to={noticePrivacyUrl} external>
            {t('directSignup.eval.form.terms.4')}
          </Link>
          {t('directSignup.eval.form.terms.5')}
          <Link to={speechTermsUrl} external>
            {t('directSignup.eval.form.terms.6')}
          </Link>
          {t('directSignup.eval.form.terms.7')}
          <Link to={privacyPolicyUrl} external>
            {t('directSignup.eval.form.terms.8')}
          </Link>
          {t('directSignup.eval.form.terms.9')}
          {t('directSignup.eval.form.terms.10')}
        </Paragraph>
      </Checkbox>

      {isEmailExistsError && <SignupEmailErrorCallout onClickLogin={onClickLogin} />}
      <FormLayout.CTA>
        <SubmitButton
          data-testid="confirm-reservation-submit-button"
          onClick={handleSubmit(async data => {
            if (!data.consentToTerms) {
              setError('consentToTerms', { message: t('errors.acceptTerms') });
              return;
            }
            const result = await submitEvalForm({
              data,
              filters,
              resumedLeadState,
              LDClient,
              flags,
              hasSwitchedToPrivatePay,
            });
            if (result.success) {
              onSuccessfulSignup(data.loginContactInfo);
            } else {
              switch (result.error) {
                case SignupErrorCode.CARD_ISSUE:
                  setError('billingInfo.stripeMethodId', {
                    message: result.message ?? t('directSignup.serverErrors.CARD_ISSUE.generic'),
                  });
                  break;
                case SignupErrorCode.EMAIL_EXISTS:
                  setError('loginContactInfo.email', {
                    message: t('directSignup.serverErrors.EMAIL_EXISTS.base'),
                  });

                  break;
                default:
                  setError('root', {
                    message: t('errors.unknownError'),
                  });
                  break;
              }
            }
          }, onInvalidSubmit)}
        >
          {t('directSignup.eval.form.submit')}
        </SubmitButton>
      </FormLayout.CTA>
    </div>
  );
};

export const DirectSignupEvaluationForm = () => {
  const { existingContactData } = useSignupDirectCtx();

  const { t } = useTranslation();

  if (existingContactData.isLoading)
    return <LoadingSpinner message={t('directSignup.loading')} className="self-center" />;

  return <Component />;
};
